import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { Layer, Source, useMap } from "react-map-gl";
import { useMapStore, usePipedriveStore } from "../..";
import { apiUrl } from "../../api/config";
import useNavigateToLocation from "../../hooks/useNavigateToLocation";
import useRadiusLocationsOnMap from "../../hooks/useRadiusLocationsOnMap";
import useVisibleLocationsOnMap from "../../hooks/useVisibleLocationsOnMap";
import { minZoom } from "../mapConfig";
import OnMapSpinner from "../spinners/OnMapSpinner";

export const locationsLayerId = "locationsLayer";

function LocationsMapLayer() {
    const map = useMap().current!;
    const navigateToLocation = useNavigateToLocation();
    const mapStore = useMapStore();
    const pipedriveStore = usePipedriveStore();
    const checkedLocations = useVisibleLocationsOnMap();
    const radiusLocations = useRadiusLocationsOnMap();
    const locations = useMemo(
        () => [...checkedLocations, ...radiusLocations],
        [checkedLocations, radiusLocations],
    );
    const [isLogosLoading, setIsLogosLoading] = useState(false);

    const locationsGeoJson: any = useMemo(() => ({
        type: "FeatureCollection",
        features: locations.length
            ? locations.map(l => ({
                type: "Feature",
                id: `${l.id}`,
                geometry: {
                    type: "Point",
                    coordinates: [l.centerPoint.longitude, l.centerPoint.latitude],
                },
                properties: {
                    latitude: l.centerPoint.latitude,
                    longitude: l.centerPoint.longitude,
                    icon: l.icon,
                },
            })) : [],
    }), [locations, mapStore.loadedLogoCompanyIds]);

    useEffect(() => {
        map.loadImage('./location.png', (error, image) => {
            if (error) throw error;
            map.addImage('location-marker', image!);
        });
    }, []);

    useEffect(() => {
        let isRequestCancelled = false;
        setIsLogosLoading(true);

        (async () => {
            const loadedLogoCompanyIds: number[] = [];

            await Promise.all(pipedriveStore.organizationsLogos.map(logo => {
                const key = `location-marker-company-${logo.organizationId}`;
                if (map.hasImage(key)) return Promise.resolve();

                return new Promise<void>(resolve => {
                    map.loadImage(`${apiUrl}/proxy/image?url=${encodeURIComponent(logo.downloadUrl)}`, (error, image) => {
                        if (error) return resolve();
                        try {
                            map.addImage(key, image!);
                            loadedLogoCompanyIds.push(logo.organizationId!);
                        } finally {
                            resolve();
                        }
                    });
                });
            }));

            if (!isRequestCancelled && loadedLogoCompanyIds.length)
                mapStore.setLoadedLogoCompanyIds([...mapStore.loadedLogoCompanyIds, ...loadedLogoCompanyIds]);
            setIsLogosLoading(false);
        })();

        return () => {
            isRequestCancelled = true;
            setIsLogosLoading(false);
        };
    }, [pipedriveStore.organizationsLogos]);

    useEffect(() => {
        const unsubscribeMouseClick = mapStore.onMouseClick("locations", feature => {
            if (feature?.layer?.id === locationsLayerId)
                navigateToLocation(Number(feature.id));
        });

        return unsubscribeMouseClick;
    }, []);

    if (!locations.length)
        return null;

    return (
        <>
            {isLogosLoading && <OnMapSpinner />}
            <Source id="locationsSource" type="geojson" data={locationsGeoJson}>
                <Layer
                    id={locationsLayerId}
                    type="symbol"
                    minzoom={minZoom.locations}
                    layout={{
                        "icon-allow-overlap": true,
                        "symbol-z-order": "source",
                        "icon-image": ["get", "icon"],
                    }}
                />
            </Source>
        </>
    );
}

export default observer(LocationsMapLayer);